/**
 * This component can render supplemental information below a given notification,
 * for example upsells, learn more links, or other supplemental information,
 * not integral to the information in the notification.
 */
import React, { Suspense, useMemo } from 'react';

import { Spinner } from '@trello/nachos/spinner';
import { useLazyComponent } from '@trello/use-lazy-component';
// eslint-disable-next-line no-restricted-imports
import { NotificationModel } from 'app/gamma/src/types/models';
import styles from './NotificationSupplement.less';
import type { NotificationType } from './NotificationsMenu.types';
import { ButlerRuleFailedBoardSupplement } from './ButlerRuleFailedBoardSupplement';
import { ButlerQuotaApproachingMemberSupplement } from './ButlerQuotaApproachingMemberSupplement';
import { ExportCompleteSupplement } from './ExportCompleteSupplement';
import { RequestAccessBoardInviteSupplement } from './RequestAccessBoardInviteSupplement';

interface NotificationSupplementProps
  extends Pick<
    NotificationModel,
    'appCreator' | 'data' | 'type' | 'idMemberCreator'
  > {
  type?: NotificationType;
}

export const NotificationSupplement: React.FunctionComponent<NotificationSupplementProps> = ({
  appCreator,
  data,
  type,
  idMemberCreator,
}) => {
  const ButlerQuotaExceededMemberSupplement = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "butler-quota-exceeded-member-supplement" */ './ButlerQuotaExceededMemberSupplement'
      ),
    {
      preload: type === 'butlerQuotaExceededMember',
      namedImport: 'ButlerQuotaExceededMemberSupplement',
    },
  );
  const ButlerQuotaExceededOrganizationSupplement = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "butler-quota-exceeded-organization-supplement" */ './ButlerQuotaExceededOrganizationSupplement'
      ),
    {
      preload: type === 'butlerQuotaExceededOrganization',
      namedImport: 'ButlerQuotaExceededOrganizationSupplement',
    },
  );

  const ButlerQuotaApproachingOrganizationSupplement = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "butler-quota-approaching-organization-supplement" */ './ButlerQuotaApproachingOrganizationSupplement'
      ),
    {
      preload: type === 'butlerQuotaApproachingOrganization',
      namedImport: 'ButlerQuotaApproachingOrganizationSupplement',
    },
  );

  const component = useMemo(() => {
    switch (type) {
      case 'butlerRuleFailedBoard': {
        if (!data?.rule) return null;
        const { rule } = data;
        return (
          <ButlerRuleFailedBoardSupplement
            application={appCreator?.name || ''}
            rule={rule}
          />
        );
      }
      case 'butlerQuotaApproachingMember': {
        return <ButlerQuotaApproachingMemberSupplement />;
      }
      case 'butlerQuotaApproachingOrganization': {
        return (
          <ButlerQuotaApproachingOrganizationSupplement
            orgId={data?.organization?.id}
          />
        );
      }
      case 'butlerQuotaExceededMember': {
        return <ButlerQuotaExceededMemberSupplement />;
      }
      case 'butlerQuotaExceededOrganization': {
        return (
          <ButlerQuotaExceededOrganizationSupplement
            orgId={data?.organization?.id}
          />
        );
      }
      case 'exportCompleteOrganization': {
        if (data?.organization?.id && data?.idExport) {
          return (
            <ExportCompleteSupplement
              organization={data.organization}
              idExport={data.idExport}
            />
          );
        }
        return null;
      }
      case 'requestAccessBoard':
      case 'requestAccessBoardWithoutOrganization':
      case 'requestAccessBoardNotInOrganization': {
        if (
          data?.board &&
          data?.board?.name &&
          data?.board?.shortLink &&
          data?.board?.id &&
          idMemberCreator
        ) {
          return (
            <RequestAccessBoardInviteSupplement
              board={data.board}
              card={data?.card}
              inviteMemberId={idMemberCreator}
            />
          );
        }
        return null;
      }
      default:
        return null;
    }
  }, [
    type,
    data,
    appCreator?.name,
    idMemberCreator,
    ButlerQuotaApproachingOrganizationSupplement,
    ButlerQuotaExceededMemberSupplement,
    ButlerQuotaExceededOrganizationSupplement,
  ]);

  if (component) {
    return (
      <Suspense
        fallback={
          <Spinner
            small
            centered
            wrapperClassName={styles.notificationSupplements}
          />
        }
      >
        <div className={styles.notificationSupplements}>{component}</div>
      </Suspense>
    );
  }
  return null;
};
